import { useState, useEffect } from 'react';
import { ArrowLeft, Save, ExternalLink, Gift } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

export default function CmsSettings({ navigate }) {
  const [tab, setTab] = useState('infos');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [displaySettings, setDisplaySettings] = useState(null);

  const load = () => fetch(`${API}/api/site/settings`).then(r => r.json()).then(setSettings).catch(() => {});
  const loadDisplay = () => fetch(`${API}/api/site/display-settings`).then(r => r.json()).then(setDisplaySettings).catch(() => {});
  useEffect(() => { load(); loadDisplay(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/site/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (displaySettings) {
        await fetch(`${API}/api/site/display-settings`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(displaySettings)
        });
      }
      toast.success('Parametres enregistres');
    } catch (e) { toast.error('Erreur'); }
    setSaving(false);
  };

  const upd = (path, val) => {
    setSettings(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = val;
      return copy;
    });
  };

  if (!settings) return <div className="text-center text-gray-400 py-12">Chargement...</div>;

  const tabs = [
    { id: 'affichage', label: 'Affichage site' },
    { id: 'infos', label: 'Infos generales' },
    { id: 'horaires', label: 'Horaires' },
    { id: 'reservation', label: 'Reservation' },
    { id: 'signatures', label: 'Signatures email' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'sections', label: 'Sections du site' },
    { id: 'textes', label: 'Textes du site' },
    { id: 'reseaux', label: 'Reseaux sociaux' },
  ];

  return (
    <div data-testid="cms-settings">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm" data-testid="settings-back-btn">
            <ArrowLeft size={16} /> Retour au site
          </button>
          <div className="flex items-center gap-2">
            <span className="text-gray-300">|</span>
            <h1 className="text-lg font-bold text-gray-900">Administration Generale</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <button onClick={() => navigate('cartes')} className="hover:text-gray-700">Carte</button>
            <button onClick={() => navigate('plateaux')} className="hover:text-gray-700">Plateaux</button>
            <button onClick={() => navigate('galerie')} className="hover:text-gray-700">Galerie</button>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            data-testid="save-settings-btn">
            <Save size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer tout'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'border-[#1a2744] text-[#1a2744]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`settings-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {tab === 'affichage' && displaySettings && <AffichageTab display={displaySettings} setDisplay={setDisplaySettings} />}
        {tab === 'infos' && <InfosTab settings={settings} upd={upd} />}
        {tab === 'horaires' && <HorairesTab settings={settings} upd={upd} />}
        {tab === 'reservation' && <ReservationTab settings={settings} upd={upd} />}
        {tab === 'whatsapp' && <WhatsAppTab settings={settings} upd={upd} />}
        {tab === 'signatures' && <SignaturesTab settings={settings} upd={upd} />}
        {tab === 'sections' && <SectionsTab settings={settings} upd={upd} />}
        {tab === 'textes' && <TextesTab settings={settings} upd={upd} />}
        {tab === 'reseaux' && <ReseauxTab settings={settings} upd={upd} />}
      </div>
    </div>
  );
}

function AffichageTab({ display, setDisplay }) {
  const upd = (key, val) => setDisplay(prev => ({ ...prev, [key]: val }));

  return (
    <div className="space-y-6" data-testid="settings-affichage">
      <h3 className="font-semibold text-gray-900">Affichage du site public</h3>

      {/* Prix carte */}
      <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
        <div>
          <p className="font-medium text-gray-900">Afficher les prix sur la carte</p>
          <p className="text-xs text-gray-400 mt-0.5">Quand desactive, seuls les noms des plats apparaissent sans les prix</p>
        </div>
        <button
          onClick={() => upd('afficher_prix_carte', !display.afficher_prix_carte)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${display.afficher_prix_carte ? 'bg-[#0d9488]' : 'bg-gray-300'}`}
          data-testid="toggle-afficher-prix">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${display.afficher_prix_carte ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Bandeau Bons Cadeaux */}
      <div className="p-5 rounded-xl border border-[#1a2744]/10 bg-[#1a2744]/[0.02] space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gift size={20} className="text-[#1a2744]" />
            <div>
              <p className="font-medium text-gray-900">Bandeau Bons Cadeaux</p>
              <p className="text-xs text-gray-400">Bandeau promotionnel affiche sur la page d'accueil du restaurant</p>
            </div>
          </div>
          <button
            onClick={() => upd('bandeau_bons_cadeaux', !display.bandeau_bons_cadeaux)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${display.bandeau_bons_cadeaux ? 'bg-[#0d9488]' : 'bg-gray-300'}`}
            data-testid="toggle-bandeau-cadeaux">
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${display.bandeau_bons_cadeaux ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {display.bandeau_bons_cadeaux && (
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Titre du bandeau</label>
              <input type="text" value={display.bandeau_texte || ''} onChange={e => upd('bandeau_texte', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                placeholder="Offrez un moment d'exception" data-testid="bandeau-texte" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Sous-titre</label>
              <input type="text" value={display.bandeau_sous_texte || ''} onChange={e => upd('bandeau_sous_texte', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                placeholder="Decouvrez nos bons cadeaux a offrir en ligne" data-testid="bandeau-sous-texte" />
            </div>

            {/* Apercu */}
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-2 font-medium">APERCU</p>
              <div className="bg-[#1a2744] rounded-lg px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Gift size={22} className="text-[#0d9488] shrink-0" />
                  <div>
                    <p className="text-white text-base font-light font-serif">{display.bandeau_texte || "Offrez un moment d'exception"}</p>
                    <p className="text-white/50 text-xs">{display.bandeau_sous_texte || "Decouvrez nos bons cadeaux"}</p>
                  </div>
                </div>
                <span className="border border-[#0d9488] text-[#0d9488] px-4 py-1.5 text-[9px] font-bold tracking-wider uppercase whitespace-nowrap rounded-sm">
                  Offrir un bon cadeau
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfosTab({ settings, upd }) {
  return (
    <div className="space-y-5" data-testid="settings-infos">
      <h3 className="font-semibold text-gray-900">Informations de contact</h3>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Nom du restaurant" value={settings.nom} onChange={v => upd('nom', v)} testId="settings-nom" />
        <Field label="Sous-titre" value={settings.sous_titre} onChange={v => upd('sous_titre', v)} testId="settings-sous-titre" />
      </div>
      <Field label="Adresse complete" value={settings.adresse} onChange={v => upd('adresse', v)} testId="settings-adresse" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Telephone" value={settings.telephone} onChange={v => upd('telephone', v)} testId="settings-telephone" />
        <Field label="Email" value={settings.email} onChange={v => upd('email', v)} testId="settings-email" />
      </div>
      <div>
        <Field label="Lien Google Maps (embed)" value={settings.google_maps_embed} onChange={v => upd('google_maps_embed', v)} testId="settings-maps" />
        <p className="text-xs text-gray-400 mt-1">Allez sur Google Maps → Partager → Integrer une carte → Copiez le lien</p>
      </div>
    </div>
  );
}

function HorairesTab({ settings, upd }) {
  const horaires = settings.horaires || {};
  return (
    <div className="space-y-4" data-testid="settings-horaires">
      <h3 className="font-semibold text-gray-900">Horaires d'ouverture</h3>
      {JOURS.map(jour => {
        const h = horaires[jour] || { ouvert: false, midi: '', soir: '' };
        return (
          <div key={jour} className="flex items-center gap-4 py-2 border-b border-gray-100">
            <label className="w-24 text-sm font-medium text-gray-700 capitalize">{jour}</label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={h.ouvert}
                onChange={e => upd(`horaires.${jour}.ouvert`, e.target.checked)}
                className="rounded border-gray-300" />
              Ouvert
            </label>
            {h.ouvert && (
              <>
                <input type="text" value={h.midi} placeholder="Midi (ex: 12:00 - 14:00)"
                  onChange={e => upd(`horaires.${jour}.midi`, e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm flex-1" />
                <input type="text" value={h.soir} placeholder="Soir (ex: 19:00 - 22:00)"
                  onChange={e => upd(`horaires.${jour}.soir`, e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm flex-1" />
              </>
            )}
            {!h.ouvert && <span className="text-sm text-gray-400 italic">Ferme</span>}
          </div>
        );
      })}
    </div>
  );
}

function ReservationTab({ settings, upd }) {
  const resa = settings.reservation || {};
  return (
    <div className="space-y-5" data-testid="settings-reservation">
      <h3 className="font-semibold text-gray-900">Parametres de reservation</h3>
      <Field label="Lien Zenchef" value={resa.zenchef_url} onChange={v => upd('reservation.zenchef_url', v)} testId="settings-zenchef" />
      <Field label="Telephone reservation" value={resa.telephone_resa} onChange={v => upd('reservation.telephone_resa', v)} testId="settings-tel-resa" />
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Message de reservation</label>
        <textarea value={resa.message_resa || ''} onChange={e => upd('reservation.message_resa', e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none" rows={3}
          placeholder="Message affiche sur la page de reservation..." data-testid="settings-msg-resa" />
      </div>
    </div>
  );
}

function TextesTab({ settings, upd }) {
  const textes = settings.textes || {};
  return (
    <div className="space-y-5" data-testid="settings-textes">
      <h3 className="font-semibold text-gray-900">Textes du site</h3>
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Description Restaurant</label>
        <textarea value={textes.description_restaurant || ''} onChange={e => upd('textes.description_restaurant', e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none" rows={4}
          data-testid="settings-desc-resto" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Description Traiteur</label>
        <textarea value={textes.description_traiteur || ''} onChange={e => upd('textes.description_traiteur', e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none" rows={4}
          data-testid="settings-desc-traiteur" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Message d'accueil</label>
        <textarea value={textes.message_accueil || ''} onChange={e => upd('textes.message_accueil', e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none" rows={3}
          data-testid="settings-msg-accueil" />
      </div>
    </div>
  );
}

function ReseauxTab({ settings, upd }) {
  const reseaux = settings.reseaux || {};
  return (
    <div className="space-y-5" data-testid="settings-reseaux">
      <h3 className="font-semibold text-gray-900">Reseaux sociaux</h3>
      <Field label="Facebook" value={reseaux.facebook} onChange={v => upd('reseaux.facebook', v)} placeholder="https://facebook.com/..." testId="settings-facebook" />
      <Field label="Instagram" value={reseaux.instagram} onChange={v => upd('reseaux.instagram', v)} placeholder="https://instagram.com/..." testId="settings-instagram" />
      <Field label="TripAdvisor" value={reseaux.tripadvisor} onChange={v => upd('reseaux.tripadvisor', v)} placeholder="https://tripadvisor.com/..." testId="settings-tripadvisor" />
      <Field label="Google Business" value={reseaux.google} onChange={v => upd('reseaux.google', v)} placeholder="https://g.page/..." testId="settings-google" />
    </div>
  );
}

function SignaturesTab({ settings, upd }) {
  const sigs = settings.signatures || {};
  const restaurant = sigs.restaurant || {};
  const traiteur = sigs.traiteur || {};

  return (
    <div className="space-y-8" data-testid="settings-signatures">
      <div>
        <h3 className="font-semibold text-gray-900 mb-1">Signatures email</h3>
        <p className="text-sm text-gray-500">Ces signatures sont utilisees dans les emails envoyes et par l'IA Cowork (agent Email).</p>
      </div>

      {/* Restaurant */}
      <div className="p-5 rounded-xl border border-[#1a2744]/10 bg-[#1a2744]/[0.02] space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full bg-[#1a2744]" />
          <h4 className="font-semibold text-[#1a2744]">Le Mas — Restaurant</h4>
        </div>
        <Field label="Nom" value={restaurant.nom} onChange={v => upd('signatures.restaurant.nom', v)} placeholder="Le Mas" testId="sig-resto-nom" />
        <Field label="Sous-titre" value={restaurant.sous_titre} onChange={v => upd('signatures.restaurant.sous_titre', v)} placeholder="Restaurant" testId="sig-resto-soustitre" />
        <Field label="Adresse" value={restaurant.adresse} onChange={v => upd('signatures.restaurant.adresse', v)} placeholder="6 Av. Marechal Joffre, 66250 Saint-Laurent-de-la-Salanque" testId="sig-resto-adresse" />
        <Field label="Telephone" value={restaurant.telephone} onChange={v => upd('signatures.restaurant.telephone', v)} placeholder="04 68 38 06 98" testId="sig-resto-tel" />
        <Field label="Email" value={restaurant.email} onChange={v => upd('signatures.restaurant.email', v)} placeholder="contact@restaurant-le-mas.fr" testId="sig-resto-email" />
        <Field label="Facebook" value={restaurant.facebook} onChange={v => upd('signatures.restaurant.facebook', v)} placeholder="https://www.facebook.com/lemasrestaurant" testId="sig-resto-fb" />
        <Field label="Instagram" value={restaurant.instagram} onChange={v => upd('signatures.restaurant.instagram', v)} placeholder="https://www.instagram.com/restaurant_le_mas" testId="sig-resto-ig" />
      </div>

      {/* Traiteur */}
      <div className="p-5 rounded-xl border border-[#0d9488]/10 bg-[#0d9488]/[0.02] space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full bg-[#0d9488]" />
          <h4 className="font-semibold text-[#0d9488]">Le Traiteur du Mas</h4>
        </div>
        <Field label="Nom" value={traiteur.nom} onChange={v => upd('signatures.traiteur.nom', v)} placeholder="Le Traiteur du Mas" testId="sig-trait-nom" />
        <Field label="Sous-titre" value={traiteur.sous_titre} onChange={v => upd('signatures.traiteur.sous_titre', v)} placeholder="Evenements & Receptions" testId="sig-trait-soustitre" />
        <Field label="Adresse" value={traiteur.adresse} onChange={v => upd('signatures.traiteur.adresse', v)} placeholder="Mariages, anniversaires, seminaires" testId="sig-trait-adresse" />
        <Field label="Telephone" value={traiteur.telephone} onChange={v => upd('signatures.traiteur.telephone', v)} placeholder="04 68 38 06 98" testId="sig-trait-tel" />
        <Field label="Email" value={traiteur.email} onChange={v => upd('signatures.traiteur.email', v)} placeholder="contact@traiteur-du-mas.fr" testId="sig-trait-email" />
        <Field label="Facebook" value={traiteur.facebook} onChange={v => upd('signatures.traiteur.facebook', v)} placeholder="https://www.facebook.com/letraiteurdelemas" testId="sig-trait-fb" />
        <Field label="Instagram" value={traiteur.instagram} onChange={v => upd('signatures.traiteur.instagram', v)} placeholder="https://www.instagram.com/traiteur_le_mas" testId="sig-trait-ig" />
      </div>
    </div>
  );
}

function SectionsTab({ settings, upd }) {
  const sections = settings.sections || {};
  const items = [
    { key: 'traiteur_prestations', label: 'Prestations Traiteur', desc: 'Page "Nos Prestations" sur le site Traiteur + bouton sur la page d\'accueil' },
  ];
  return (
    <div className="space-y-5" data-testid="settings-sections">
      <h3 className="font-semibold text-gray-900">Sections du site public</h3>
      <p className="text-sm text-gray-500">Activez ou desactivez les sections visibles sur le site public.</p>
      {items.map(item => {
        const enabled = sections[item.key] !== false;
        return (
          <div key={item.key} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">{item.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
            </div>
            <button
              onClick={() => upd(`sections.${item.key}`, !enabled)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${enabled ? 'bg-[#0d9488]' : 'bg-gray-300'}`}
              data-testid={`toggle-${item.key}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function WhatsAppTab({ settings, upd }) {
  const wa = settings.whatsapp || { enabled: true, number: '', message: '' };
  return (
    <div className="space-y-6" data-testid="settings-whatsapp">
      <h3 className="font-semibold text-gray-900">Bouton WhatsApp</h3>
      <p className="text-sm text-gray-500">Gerez le bouton WhatsApp flottant visible sur toutes les pages du site public.</p>

      {/* Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${wa.enabled ? 'bg-[#25D366]' : 'bg-gray-300'}`}>
            <svg viewBox="0 0 32 32" className="w-5 h-5 fill-white">
              <path d="M16.004 0h-.008C7.174 0 .002 7.174.002 16c0 3.5 1.128 6.744 3.046 9.378L1.06 31.502l6.318-1.958A15.907 15.907 0 0016.004 32C24.826 32 32 24.826 32 16S24.826 0 16.004 0zm9.302 22.602c-.39 1.1-1.932 2.014-3.168 2.282-.846.18-1.95.324-5.67-1.218-4.762-1.972-7.826-6.81-8.064-7.126-.23-.316-1.932-2.574-1.932-4.908s1.224-3.482 1.658-3.96c.434-.478.948-.598 1.264-.598.316 0 .632.002.908.016.292.014.682-.11 1.068.814.39.94 1.328 3.244 1.446 3.48.118.236.196.51.04.826-.158.316-.236.512-.472.788-.236.276-.496.616-.71.826-.236.236-.482.492-.208.964.276.472 1.224 2.02 2.63 3.272 1.808 1.608 3.332 2.106 3.804 2.342.472.236.748.196 1.024-.118.276-.316 1.184-1.382 1.5-1.86.316-.478.632-.394 1.068-.236.434.158 2.76 1.302 3.234 1.538.472.236.788.354.906.552.118.196.118 1.146-.274 2.248z"/>
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">Bouton WhatsApp sur le site</p>
            <p className="text-xs text-gray-400">{wa.enabled ? 'Visible sur toutes les pages' : 'Masque du site'}</p>
          </div>
        </div>
        <button
          onClick={() => upd('whatsapp.enabled', !wa.enabled)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${wa.enabled ? 'bg-[#25D366]' : 'bg-gray-300'}`}
          data-testid="whatsapp-toggle">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${wa.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Number */}
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Numero WhatsApp</label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">+</span>
          <input type="text" value={wa.number || ''} onChange={e => upd('whatsapp.number', e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
            placeholder="33468380698 (sans + ni espaces)"
            data-testid="whatsapp-number-input" />
        </div>
        <p className="text-xs text-gray-400 mt-1">Format international sans le +, ex: 33612345678</p>
      </div>

      {/* Default message */}
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">Message d'accroche</label>
        <input type="text" value={wa.message || ''} onChange={e => upd('whatsapp.message', e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
          placeholder="Bonjour Le Mas !"
          data-testid="whatsapp-message-input" />
        <p className="text-xs text-gray-400 mt-1">Message pre-rempli quand le client clique sur le bouton</p>
      </div>

      {/* Preview */}
      {wa.enabled && wa.number && (
        <div className="p-4 rounded-lg border border-dashed border-gray-200 bg-white">
          <p className="text-xs text-gray-400 mb-2 font-medium">APERCU</p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center shadow-md flex-shrink-0">
              <svg viewBox="0 0 32 32" className="w-6 h-6 fill-white">
                <path d="M16.004 0h-.008C7.174 0 .002 7.174.002 16c0 3.5 1.128 6.744 3.046 9.378L1.06 31.502l6.318-1.958A15.907 15.907 0 0016.004 32C24.826 32 32 24.826 32 16S24.826 0 16.004 0zm9.302 22.602c-.39 1.1-1.932 2.014-3.168 2.282-.846.18-1.95.324-5.67-1.218-4.762-1.972-7.826-6.81-8.064-7.126-.23-.316-1.932-2.574-1.932-4.908s1.224-3.482 1.658-3.96c.434-.478.948-.598 1.264-.598.316 0 .632.002.908.016.292.014.682-.11 1.068.814.39.94 1.328 3.244 1.446 3.48.118.236.196.51.04.826-.158.316-.236.512-.472.788-.236.276-.496.616-.71.826-.236.236-.482.492-.208.964.276.472 1.224 2.02 2.63 3.272 1.808 1.608 3.332 2.106 3.804 2.342.472.236.748.196 1.024-.118.276-.316 1.184-1.382 1.5-1.86.316-.478.632-.394 1.068-.236.434.158 2.76 1.302 3.234 1.538.472.236.788.354.906.552.118.196.118 1.146-.274 2.248z"/>
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-700">Ouvre wa.me/<strong>{wa.number}</strong></p>
              <p className="text-xs text-gray-400">"{wa.message}"</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">{label}</label>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
        placeholder={placeholder || ''} data-testid={testId} />
    </div>
  );
}
